// "(edited)" sits next to "Started work at <time>" / "Ended work at <time>" and
// fires when the user has revised any meaningful check-in / check-out content —
// time, notes, audio, or images. Backend includes fields in deltas even when
// the value didn't change, so we compare values, not just key presence.
const CHECK_IN_FIELDS = ['checkInTime', 'checkInNotes', 'checkInAudio', 'checkInImages'];
const CHECK_OUT_FIELDS = ['checkOutTime', 'checkOutNotes', 'checkOutAudio', 'checkOutImages'];

const sectionFields = (section: 'checkIn' | 'checkOut') =>
	section === 'checkIn' ? CHECK_IN_FIELDS : CHECK_OUT_FIELDS;

// Normalize timestamps to second precision — the card displays `h:mm a` so
// sub-second differences (e.g. backend serializing `...14.057Z` then `...14.000Z`)
// shouldn't count as edits.
const normalizeForCompare = (fieldName: string, value: any): any => {
	if (typeof value !== 'string') return value;
	if (fieldName === 'checkInTime' || fieldName === 'checkOutTime') {
		const date = new Date(value);
		if (isNaN(date.getTime())) return value;
		return Math.floor(date.getTime() / 1000);
	}
	return value;
};

const valuesEqual = (fieldName: string, left: any, right: any): boolean => {
	const a = normalizeForCompare(fieldName, left);
	const b = normalizeForCompare(fieldName, right);
	if (a === b) return true;
	try {
		return JSON.stringify(a) === JSON.stringify(b);
	} catch (_err) {
		return false;
	}
};

const findChange = (revision: any, fieldName: string) => {
	const changes = Array.isArray(revision && revision.changes) ? revision.changes : [];
	return changes.find((change: any) => change.fieldName === fieldName);
};

const findPriorValue = (revisions: any[], targetRevisionNumber: number, fieldName: string) => {
	const priorRevisions = revisions
		.filter((revision: any) => revision.revision < targetRevisionNumber)
		.sort((left: any, right: any) => right.revision - left.revision);
	for (const revision of priorRevisions) {
		const change = findChange(revision, fieldName);
		if (change) return change.newValue;
	}
	return undefined;
};

const fieldHasMeaningfulChange = (revisions: any[], fieldName: string): boolean => {
	const sorted = [...revisions].sort((left: any, right: any) => left.revision - right.revision);
	let previousValue: any = undefined;
	for (const revision of sorted) {
		const change = findChange(revision, fieldName);
		if (!change) continue;
		const valueChanged = !valuesEqual(fieldName, previousValue, change.newValue);
		if (revision.revision > 1 && valueChanged) return true;
		previousValue = change.newValue;
	}
	return false;
};

export const wasServiceCallSectionEdited = (
	serviceCall: any,
	section: 'checkIn' | 'checkOut'
): boolean => {
	const revisions = serviceCall && serviceCall.revisionChanges;
	if (!Array.isArray(revisions) || revisions.length <= 1) return false;
	return sectionFields(section).some((fieldName) => fieldHasMeaningfulChange(revisions, fieldName));
};

export const wasRevisionSectionEdited = (
	revisions: any[],
	targetRevisionNumber: number,
	section: 'checkIn' | 'checkOut'
): boolean => {
	if (!Array.isArray(revisions) || targetRevisionNumber <= 1) return false;
	const targetRevision = revisions.find(
		(revision: any) => revision.revision === targetRevisionNumber
	);
	if (!targetRevision) return false;
	return sectionFields(section).some((fieldName) => {
		const change = findChange(targetRevision, fieldName);
		if (!change) return false;
		const priorValue = findPriorValue(revisions, targetRevisionNumber, fieldName);
		return priorValue !== undefined && !valuesEqual(fieldName, priorValue, change.newValue);
	});
};
